import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Search,
  ArrowLeft,
  ReceiptText,
  Flame,
  BellRing,
  CheckCircle2,
  XCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: "new", label: "Order Placed", icon: ReceiptText },
  { id: "preparing", label: "In the Kitchen", icon: Flame },
  { id: "ready", label: "Ready to Serve", icon: BellRing },
  { id: "completed", label: "Completed", icon: CheckCircle2 },
];

const fmtTime = (iso) =>
  new Date(iso).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });

const Track = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const activeQuery = useRef("");

  const search = async (q, silent = false) => {
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      const res = await axios.get(`${API}/orders/track`, { params: { query: q } });
      setResults(res.data);
      if (res.data.length === 0) setError("No orders found for that number — check and try again.");
    } catch (err) {
      if (!silent) setError(err.response?.data?.detail || "Could not look up your order right now.");
      if (err.response?.status !== 400 && !silent) setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    activeQuery.current = query.trim();
    search(activeQuery.current);
  };

  useEffect(() => {
    const t = setInterval(() => {
      if (activeQuery.current) search(activeQuery.current, true);
    }, 15000);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="track-page" className="min-h-screen bg-[#0A0A0C] text-[#F5F2EB]">
      <header className="glass-nav sticky top-0 z-40">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-5">
          <a href="/" className="flex items-center gap-3" data-testid="track-home-link">
            <img src="/assets/mark.png" alt="Podi Sambaram" className="h-9 w-9 rounded-full object-cover" />
            <span className="font-display text-lg font-semibold">Podi Sambaram</span>
          </a>
          <a
            href="/"
            data-testid="track-back-link"
            className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs text-zinc-300 transition-colors hover:border-[#F57C00]/50 hover:text-[#FF9800]"
          >
            <ArrowLeft size={13} /> Back to site
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-12">
        <p className="text-center font-mono-accent text-xs uppercase tracking-[0.3em] text-[#F57C00]">
          Live Order Tracking
        </p>
        <h1 className="mt-3 text-center font-display text-3xl font-semibold sm:text-4xl">
          Where is my <span className="text-gradient-saffron italic">order?</span>
        </h1>
        <p className="mx-auto mt-3 max-w-md text-center text-sm text-zinc-400">
          Enter your order number (like PS0253C6) or the phone number you ordered with.
          Status refreshes automatically.
        </p>

        <form
          data-testid="track-form"
          onSubmit={submit}
          className="mx-auto mt-8 flex max-w-lg items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-2 transition-colors focus-within:border-[#F57C00]/50"
        >
          <Search size={16} className="shrink-0 text-zinc-500" />
          <input
            data-testid="track-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Order number or phone number"
            className="w-full bg-transparent py-2 text-sm text-zinc-100 placeholder-zinc-500 outline-none"
          />
          <button
            data-testid="track-search-button"
            type="submit"
            disabled={loading || query.trim().length < 4}
            className="shrink-0 rounded-full bg-[#F57C00] px-5 py-2.5 font-grotesk text-sm font-semibold text-black transition-all hover:bg-[#FF9800] disabled:opacity-50"
          >
            {loading ? "..." : "Track"}
          </button>
        </form>

        {error && (
          <p data-testid="track-error" className="mt-6 text-center text-sm text-red-400">
            {error}
          </p>
        )}

        {results && results.length > 0 && (
          <div className="mt-10 space-y-5">
            {results.map((o) => {
              const cancelled = o.status === "cancelled";
              const stepIndex = STEPS.findIndex((s) => s.id === o.status);
              return (
                <article
                  key={o.id}
                  data-testid={`track-result-${o.order_no}`}
                  className="card-surface rounded-3xl p-6"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="font-mono-accent text-xl font-bold text-[#FFB74D]">{o.order_no}</span>
                      <span className="rounded-full border border-white/10 px-3 py-1 text-[11px] text-zinc-400">
                        {o.order_type === "dine_in"
                          ? `Dine-In${o.table_number ? ` • Table ${o.table_number}` : ""}`
                          : "Takeaway"}
                      </span>
                    </div>
                    <span className="text-xs text-zinc-500">{fmtTime(o.created_at)}</span>
                  </div>

                  {cancelled ? (
                    <div
                      data-testid={`track-cancelled-${o.order_no}`}
                      className="mt-5 flex items-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300"
                    >
                      <XCircle size={16} /> This order was cancelled. Call us at +91 92176 12215 for help.
                    </div>
                  ) : (
                    <div data-testid={`track-timeline-${o.order_no}`} className="mt-6 flex items-center">
                      {STEPS.map((s, i) => {
                        const done = i <= stepIndex;
                        const current = i === stepIndex;
                        return (
                          <div key={s.id} className="flex flex-1 items-center last:flex-none">
                            <div className="flex flex-col items-center gap-1.5">
                              <div
                                className={`flex h-10 w-10 items-center justify-center rounded-full border transition-all ${
                                  done
                                    ? "border-[#F57C00] bg-[#F57C00]/15 text-[#FF9800]"
                                    : "border-white/10 text-zinc-600"
                                } ${current ? "order-glow" : ""}`}
                              >
                                <s.icon size={16} />
                              </div>
                              <span
                                className={`text-center font-mono-accent text-[9px] uppercase tracking-wider sm:text-[10px] ${
                                  done ? "text-[#FFB74D]" : "text-zinc-600"
                                }`}
                              >
                                {s.label}
                              </span>
                            </div>
                            {i < STEPS.length - 1 && (
                              <div className={`mx-1 mb-5 h-px flex-1 sm:mx-2 ${i < stepIndex ? "bg-[#F57C00]" : "bg-white/10"}`} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <ul className="mt-5 space-y-1 border-t border-white/8 pt-4 text-sm text-zinc-400">
                    {o.items.map((i) => (
                      <li key={i.name} className="flex justify-between gap-3">
                        <span>{i.qty} × {i.name}</span>
                        <span className="font-mono-accent text-zinc-300">₹{i.price * i.qty}</span>
                      </li>
                    ))}
                    {o.delivery_fee > 0 && (
                      <li className="flex justify-between gap-3 text-amber-300">
                        <span>Delivery fee (beyond 3 KM)</span>
                        <span className="font-mono-accent">₹{o.delivery_fee}</span>
                      </li>
                    )}
                  </ul>

                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-white/8 pt-4">
                    {o.payment_status === "paid" ? (
                      <span className="rounded-full border border-[#4CAF50]/50 bg-[#388E3C]/15 px-3.5 py-1.5 font-mono-accent text-[11px] font-semibold uppercase tracking-wider text-[#81C784]">
                        Payment Received
                      </span>
                    ) : (
                      <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-3.5 py-1.5 font-mono-accent text-[11px] font-semibold uppercase tracking-wider text-amber-300">
                        Pay at Counter / UPI
                      </span>
                    )}
                    <span className="font-mono-accent text-lg font-bold text-[#FFB74D]">₹{o.total}</span>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default Track;
